import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import type { Database } from '../lib/database.types'

// `supabase.from(table)` narrows its return type on the table literal, which
// means callers of this generic helper can't satisfy the union of every
// table's Insert/Update shape. We cast once to a narrower structural interface
// that describes exactly the subset of the PostgrestClient surface this hook
// uses — still an escape from the library's table-literal narrowing, but it
// documents the API contract (select/insert/update/delete + order/eq/single)
// rather than a blanket `any`. The per-table adapter (useTags/useCategories)
// still owns the row-body correctness via its `rowForInsert` / `rowForUpdate`.
interface DynamicResult<Row> {
  data: Row | null
  error: { message: string } | null
}
interface DynamicInsertBuilder<Row> extends PromiseLike<DynamicResult<Row[]>> {
  select(cols: string): DynamicInsertBuilder<Row> & {
    single(): PromiseLike<DynamicResult<Row>>
  }
}
interface DynamicUpdateBuilder<Row> extends PromiseLike<DynamicResult<Row[]>> {
  eq(col: string, value: unknown): DynamicUpdateBuilder<Row>
}
interface DynamicDeleteBuilder<Row> extends PromiseLike<DynamicResult<Row[]>> {
  eq(col: string, value: unknown): DynamicDeleteBuilder<Row>
}
interface DynamicSelectBuilder<Row> extends PromiseLike<DynamicResult<Row[]>> {
  order(col: string, opts?: { ascending?: boolean }): DynamicSelectBuilder<Row>
  eq(col: string, value: unknown): DynamicSelectBuilder<Row>
  limit(n: number): DynamicSelectBuilder<Row>
}
interface DynamicTableBuilder<Row> {
  select(cols: string): DynamicSelectBuilder<Row>
  insert(row: Record<string, unknown> | Record<string, unknown>[]): DynamicInsertBuilder<Row>
  update(row: Record<string, unknown>): DynamicUpdateBuilder<Row>
  delete(): DynamicDeleteBuilder<Row>
}
interface DynamicSupabase {
  from<Row = Record<string, unknown>>(table: string): DynamicTableBuilder<Row>
}
const anySupabase = supabase as unknown as DynamicSupabase

/**
 * Generic user-scoped CRUD hook backing `useTags`, `useCategories`, and any
 * future "row per user with an id/name/color/sort_order" table.
 *
 * Why this exists: the previous `useTags` and `useCategories` were 95%
 * identical — same select-all query, same user_id guard, same optimistic
 * add with temp-id swap, same snapshot/rollback on update/delete. Every
 * time we added a new user-scoped crud surface we either re-typed the
 * boilerplate or skipped the optimistic polish. Centralising here means:
 *
 *   - One place to fix a cache-update bug.
 *   - One place to tune the optimistic contract (snapshot rollback keys,
 *     temp-id reconciliation, RLS error messaging).
 *   - New user-scoped tables get CRUD in ~10 lines of wiring.
 *
 * Intentionally unopinionated about:
 *   - Field shape — caller picks the Row type and select list.
 *   - Sort — caller passes an `orderBy` column name; defaults to sort_order.
 *   - Insert shape — caller provides `rowForInsert(input, user_id, order)`.
 *
 * Does NOT cover:
 *   - Assignment tables (tag_assignments, category_assignments). Their
 *     unique constraint + "upsert-if-missing, delete-if-exists" semantics
 *     are different enough that a shared helper would have more special
 *     cases than code. They stay bespoke.
 */

type TableName = keyof Database['public']['Tables']

/** Sentinel empty list used as the "no data yet" return. Module-level so
 * every hook call returns the same reference pre-fetch — downstream
 * `useMemo([items])` stays stable through the loading state. */
const EMPTY_LIST: readonly unknown[] = Object.freeze([])

export interface UserScopedItem {
  id: string
  sort_order: number
}

interface Config<T extends UserScopedItem, AddInput, UpdateInput> {
  table: TableName
  /** Comma-separated columns to select, matching `T`'s shape. */
  select: string
  /** Column used for ORDER BY (default: `sort_order`). */
  orderBy?: string
  /** TanStack Query key factory (already user-scoped by the caller). */
  queryKey: (userId: string | undefined) => QueryKey
  /** Build the insert row body from caller input + user_id + next sort_order. */
  rowForInsert: (input: AddInput, userId: string, nextSortOrder: number) => Record<string, unknown>
  /** Build the update row body from caller input. */
  rowForUpdate: (input: UpdateInput) => Record<string, unknown>
  /**
   * Build an optimistic in-cache row (with a temp id) that matches the full
   * `T` shape. When provided, `add()` inserts this row immediately and swaps
   * the server-returned row in on success (or rolls back on error). Skip it
   * and the hook falls back to the onSuccess-append path — safe, but with
   * a perceptible delay on slow networks.
   *
   * The temp id is surfaced so callers can retain it if they need to (e.g.
   * to scroll a newly-created row into view before the real id arrives).
   */
  optimisticRow?: (input: AddInput, tempId: string, nextSortOrder: number) => T
}

export interface UserScopedCrud<T extends UserScopedItem, AddInput, UpdateInput> {
  items: T[]
  loading: boolean
  error: string | null
  add: (input: AddInput) => Promise<T>
  update: (id: string, input: UpdateInput) => Promise<void>
  remove: (id: string) => Promise<void>
}

export function useUserScopedCrud<
  T extends UserScopedItem,
  AddInput,
  UpdateInput,
>(config: Config<T, AddInput, UpdateInput>): UserScopedCrud<T, AddInput, UpdateInput> {
  const { user } = useAuth()
  const qc = useQueryClient()
  // Pin the pieces of `config` the hook actually consumes in useMemo /
  // useQuery dep arrays. Callers pass `config` as an inline object that's
  // a new reference every render — depending on the whole thing defeats
  // memoisation. The individual fields are either primitives (table,
  // select, orderBy) or stable-function references (queryKey), so naming
  // them out keeps the deps honest AND stable.
  const { queryKey, table, select, orderBy } = config
  const key = useMemo(() => queryKey(user?.id), [queryKey, user?.id])
  const orderColumn = orderBy ?? 'sort_order'

  const query = useQuery<T[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await anySupabase
        .from(table)
        .select(select)
        .order(orderColumn)
      if (error) throw new Error(error.message)
      return ((data ?? []) as unknown) as T[]
    },
  })

  const addMut = useMutation({
    mutationKey: [config.table, 'add'],
    mutationFn: async (input: AddInput): Promise<T> => {
      if (!user) throw new Error('Not authenticated')

      // Compute `nextSortOrder` and stamp the optimistic row in a single
      // cache updater so two concurrent add() calls can't both read the
      // same pre-insert max and mint colliding sort_order values. React
      // Query calls the updater with the latest cache snapshot, and we
      // read `maxOrder` *inside* that snapshot — so the second concurrent
      // call sees the first call's already-appended optimistic row and
      // picks the next slot up.
      const tempId = config.optimisticRow ? `tmp_${crypto.randomUUID()}` : null
      let nextSortOrder = 0
      if (config.optimisticRow && tempId) {
        qc.setQueryData<T[]>(key, (prev) => {
          const list = prev ?? []
          const maxOrder = list.reduce((max, t) => Math.max(max, t.sort_order), -1)
          nextSortOrder = maxOrder + 1
          const optimistic = config.optimisticRow!(input, tempId, nextSortOrder)
          return [...list, optimistic]
        })
      } else {
        // Non-optimistic path still needs a sort_order for the insert.
        const current = qc.getQueryData<T[]>(key) ?? []
        const maxOrder = current.reduce((max, t) => Math.max(max, t.sort_order), -1)
        nextSortOrder = maxOrder + 1
      }

      const { data, error } = await anySupabase
        .from(config.table)
        .insert(config.rowForInsert(input, user.id, nextSortOrder))
        .select(config.select)
        .single()

      if (error) {
        if (tempId) {
          qc.setQueryData<T[]>(key, (prev) => (prev ?? []).filter((t) => t.id !== tempId))
        }
        throw new Error(error.message)
      }
      if (!data) {
        if (tempId) {
          qc.setQueryData<T[]>(key, (prev) => (prev ?? []).filter((t) => t.id !== tempId))
        }
        throw new Error('insert returned no row')
      }

      const inserted = data as unknown as T
      if (tempId) {
        // Swap temp-id row for the real one in place so list ordering is
        // preserved without a re-sort flash. Also filter any pre-existing
        // row that matches the real id: that happens when RealtimeBridge
        // fans in the server-side INSERT between our request and response,
        // which would otherwise leave a duplicate (temp-id row stays, real
        // row also present because the filter only matches tempId).
        qc.setQueryData<T[]>(key, (prev) => {
          const next = (prev ?? [])
            // Drop any realtime-inserted copy of the server row to avoid
            // having both the real and the temp rows side-by-side.
            .filter((t) => t.id !== inserted.id || t.id === tempId)
            .map((t) => (t.id === tempId ? inserted : t))
          return next
        })
      }
      return inserted
    },
    // Non-optimistic fallback: append on success. When `optimisticRow` is
    // provided the mutationFn has already updated the cache, so this is a
    // no-op in that path.
    onSuccess: (inserted) => {
      if (config.optimisticRow) return
      qc.setQueryData<T[]>(key, (prev) => [...(prev ?? []), inserted])
    },
  })

  const updateMut = useMutation({
    mutationKey: [config.table, 'update'],
    mutationFn: async ({ id, input }: { id: string; input: UpdateInput }) => {
      // Snapshot → optimistic patch → rollback on error. The snapshot is the
      // entire list, not just the touched row — cheaper than diffing and the
      // lists here are all user-scoped preferences that stay well under a
      // few hundred rows.
      const snapshot = qc.getQueryData<T[]>(key)
      qc.setQueryData<T[]>(key, (prev) =>
        (prev ?? []).map((t) => (t.id === id ? { ...t, ...config.rowForUpdate(input) } as T : t)),
      )
      const { error } = await anySupabase
        .from(config.table)
        .update(config.rowForUpdate(input))
        .eq('id', id)
      if (error) {
        qc.setQueryData<T[]>(key, snapshot)
        throw new Error(error.message)
      }
    },
  })

  const deleteMut = useMutation({
    mutationKey: [config.table, 'delete'],
    mutationFn: async (id: string) => {
      const snapshot = qc.getQueryData<T[]>(key)
      qc.setQueryData<T[]>(key, (prev) => (prev ?? []).filter((t) => t.id !== id))
      const { error } = await anySupabase.from(config.table).delete().eq('id', id)
      if (error) {
        qc.setQueryData<T[]>(key, snapshot)
        throw new Error(error.message)
      }
    },
  })

  const add = useCallback((input: AddInput) => addMut.mutateAsync(input), [addMut])
  const update = useCallback(
    (id: string, input: UpdateInput) => updateMut.mutateAsync({ id, input }),
    [updateMut],
  )
  const remove = useCallback((id: string) => deleteMut.mutateAsync(id), [deleteMut])

  // Stable empty-array identity when `query.data` is undefined (pre-
  // fetch, errored state). A naive `query.data ?? []` allocates a fresh
  // `[]` every render, breaking downstream useMemo/useEffect deps that
  // reference `items`. The module-level EMPTY_LIST below keeps the
  // identity stable until real data arrives.
  const items = query.data ?? EMPTY_LIST

  return {
    items: items as T[],
    loading: !!user && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    add,
    update,
    remove,
  }
}
